import type {
	Style,
	Gradient,
	ColorMap,
	ThemeFontColorMap,
	ThemeFontMap,
	UnresolvedVariantRelation,
	Spx,
} from '@wix/thunderbolt-becky-types'
import type { SkinDefaultsData } from './skinsDefaultProperties'
import { deprecatedSkinsFallbacks } from './deprecatedSkinsFallbacks'
import { fontObjectToString, getFontClass, parseFontStr } from '../fonts'
import { color, resolveSpxAttribute } from '@wix/thunderbolt-commons'
import { fixOriginalFont } from '../fonts/fontFixer'
import { getTagName, getTagAttributes, getInlineStyle, toInlineStyle, toHtmlOpenTag } from '../elementUtils'
import { isUndefined, kebabCase } from 'lodash'
import { componentTypeToSkins } from './compsValidSkins'
import { removeHash } from '../../functionLibrary'
import { resolveWixMediaUrl } from './wixMediaUrl'

// This value is hardcoded and it is a contract between Viewer and Components - DON'T CHANGE IT.
// All static scss is designed relatively to this number
const SPX_CONTAINER_WIDTH_FOR_STATIC_CSS = 1000
const transformToPixelValue = (propValue: string) => `${parseInt(propValue, 10)}px`
const isUnitless = (value: string) => /^[0-9]*\.?[0-9]*$/.test(value)

const addPx = (value: string): string => (isUnitless(value) ? transformToPixelValue(value) : value)
const fixBorder = (borderValue: string, isSiteFromStudio: boolean): string => {
	return borderValue
		.split(' ')
		.map((propValue) => {
			return isSiteFromStudio
				? isUnitless(propValue)
					? transformToPixelValue(propValue)
					: propValue
				: transformToPixelValue(propValue)
		})
		.join(' ')
}

export const isThemeFont = (value: string) => /^font_\d+$/.test(value)

const getFontWeight = (value: string): string => {
	if (value === 'bold') {
		return '700'
	} else if (value === 'normal') {
		return 'normal'
	} else {
		const weightAsNumber = parseInt(value, 10)

		if (!Number.isNaN(weightAsNumber)) {
			return `${weightAsNumber}`
		}
	}

	return 'normal'
}

const applyPropertiesOverrides = (
	value: string,
	themeFontMap: ThemeFontMap,
	propertiesOverride: { [id: string]: string },
	isSiteFromStudio = false
) => {
	const themeFontValue = themeFontMap[value]
	const fontObject = parseFontStr(themeFontValue)
	Object.keys(propertiesOverride).forEach((key) => {
		const overrideValue = propertiesOverride[key]
		if (key === 'fontSize') {
			const sizeValue = overrideValue.replace(/[a-z|%]+/, '')
			const sizeUnit = overrideValue.replace(sizeValue, '')
			fontObject.size = parseInt(sizeValue, 10)
			fontObject.sizeUnit = sizeUnit
		} else if (key === 'fontFamily') {
			fontObject.family = [overrideValue]
		} else if (key === 'fontWeight') {
			fontObject.weight = getFontWeight(overrideValue)
		} else if (key === 'fontStyle') {
			fontObject.style = overrideValue
		}
	})
	const fontString = fontObjectToString(fontObject, isSiteFromStudio)
	const fixedOriginalFont = fixOriginalFont({
		font: fontString,
		respectSizeUnit: isSiteFromStudio,
	})
	return fixedOriginalFont
}

const hasEqualParentheses = (input: string) => {
	let leftCount = 0
	let rightCount = 0
	for (const char of input) {
		if (char === '(') {
			leftCount++
		} else if (char === ')') {
			rightCount++
		}
	}
	return leftCount === rightCount
}

const isKeywordFont = (font: string) => ['inherit', 'initial', 'revert', 'revert-layer', 'unset'].includes(font)

const toValidCssColor = (value: string) => {
	const isThemeColorOnly = /^color_\d+$/.test(value)
	return isThemeColorOnly ? `rgba(var(--${value}),1)` : value
}

const getProperties = (
	spx: Spx | undefined,
	styleProperties: Style['style']['properties'],
	defaultProperties: { types: Record<string, string>; defaults: Record<string, string> },
	isSiteFromStudio: boolean,
	skin: string,
	staticMediaUrl: string,
	themeFontMap?: ThemeFontMap,
	stylePropertiesOverride?: Style['style']['propertiesOverride']
): Record<string, string> => {
	const styleWithDefaults = {
		...defaultProperties.defaults,
		...styleProperties,
	}

	const getMappedAlphaParamValue = (colorParamKey: string): string | null => {
		// color has own alpha value, ignore mapped param
		if (styleWithDefaults[`alpha-${colorParamKey}`]) {
			return null
		}

		const maybeMappedParamKey = defaultProperties.defaults[colorParamKey]
		const isMappedParam = maybeMappedParamKey && styleWithDefaults.hasOwnProperty(maybeMappedParamKey)

		return isMappedParam ? styleWithDefaults[`alpha-${maybeMappedParamKey}`] : null
	}

	const fixThemeColorValue = (colorVal: string) => {
		const match = colorVal.match(/^{(?<themeColor>color_\d+)}$/)
		const themeColor = match?.groups!.themeColor
		return themeColor ? themeColor : colorVal
	}

	const fixDuplicateRgbValue = (colorVal: string) => {
		const match = colorVal.match(/rgb\(rgb\((?<param>\d+,\d+,\d+)\)/)
		const rgbValue = match?.groups!.param
		return rgbValue ? `rgb(${rgbValue})` : colorVal
	}

	return Object.entries(styleWithDefaults).reduce<Record<string, string>>((acc, [key, currentValue]) => {
		let value = currentValue

		if (isUndefined(value)) {
			return acc
		}

		const applyOverrides = (themeFont: string) => {
			if (themeFontMap && stylePropertiesOverride && stylePropertiesOverride[key]) {
				return applyPropertiesOverrides(themeFont, themeFontMap, stylePropertiesOverride[key], isSiteFromStudio)
			}
			return themeFont
		}

		while (styleWithDefaults[value] && value !== styleWithDefaults[value]) {
			value = styleWithDefaults[value]
		}

		const type = defaultProperties.types[key]
		const isSkinParam = !isUndefined(type)

		if (isSiteFromStudio && skin === 'DefaultAreaSkin' && type === 'BOX_SHADOW' && !hasEqualParentheses(value)) {
			value = 'none' // todo - remove once https://jira.wixpress.com/browse/EDIX-22427 is resolved
		}
		const fixedKey = key[0] === '$' ? key.slice(1) : key
		if (isSiteFromStudio && isSkinParam && type !== 'FONT' && type !== 'BORDER' && `${value}`.includes('spx')) {
			value = resolveSpxAttribute(value, spx, true)
		}
		switch (type) {
			case 'URL':
				break
			case 'SIZE':
				acc[fixedKey] = addPx(`${value}`)
				break
			case 'BORDER':
				const fixedBorder =
					styleProperties && styleProperties[key]
						? fixBorder(`${value}`, isSiteFromStudio)
						: addPx(`${value}`)
				acc[fixedKey] = isSiteFromStudio ? resolveSpxAttribute(fixedBorder, spx, true) : fixedBorder
				break
			case 'FONT':
				if (isKeywordFont(value)) {
					acc[fixedKey] = value
					break
				}
				const fixedFont = isThemeFont(value)
					? applyOverrides(value)
					: fixOriginalFont({ font: value, respectSizeUnit: isSiteFromStudio })
				acc[fixedKey] = isSiteFromStudio ? resolveSpxAttribute(fixedFont, spx, true) : fixedFont
				break
			case 'COLOR':
				acc[fixedKey] = fixDuplicateRgbValue(fixThemeColorValue(value))
				const mappedAlpha = getMappedAlphaParamValue(key)
				if (mappedAlpha) {
					acc[`alpha-${fixedKey}`] = mappedAlpha
				}
				break
			case 'CSS_COLOR':
				acc[fixedKey] = toValidCssColor(value)
				break
			case 'FILL_LAYERS':
			case 'BACKGROUND_FILL':
				acc[fixedKey] = handleFillLayers(value, staticMediaUrl)
				break
			case 'BLEND_MODE':
				acc[fixedKey] = kebabCase(value)
				break
			default:
				acc[fixedKey] = value
		}

		return acc
	}, {})
}

const handleFillLayers = (fill: string, staticMediaUrl: string) => handleImageLayers(fill, staticMediaUrl)

const handleImageLayers = (fill: string, staticMediaUrl: string) => {
	// Find all image src's, with the pattern of "wixMediaUrl(url: string, width: number as string, height: number as string)"
	const captureImageLayersRegex = /wixMediaUrl\(([^,]+),\s*(\d+),\s*(\d+)\)/g
	// Extract just the args wrapped with "wixMediaUrl(...)"
	const captureArgsRegex = /\(([^)]*)\)/
	const wixMediaUrls = fill.match(captureImageLayersRegex) || []
	const resolvedMediaUrls = wixMediaUrls.map((wixMediaUrl) => {
		const args = wixMediaUrl.match(captureArgsRegex)?.[1] || ''
		const [url, width, height] = args.split(',').map((arg) => arg.trim())
		return resolveWixMediaUrl(staticMediaUrl, url, { width, height })
	})
	return resolvedMediaUrls?.reduce((resolvedFill, mediaUrl, index) => {
		return resolvedFill.replace(wixMediaUrls[index], mediaUrl)
	}, fill)
}

const last = (array: Array<string>) => array[array.length - 1]

export const fixStyleDataItem = (
	style: Style,
	componentClassName: string,
	staticMediaUrl: string,
	themeFontMap?: ThemeFontMap,
	isSiteFromStudio = false,
	componentsSkinParams: Record<string, SkinDefaultsData> = {}
): Style => {
	if (!style.skin || style.skin.startsWith('svgshape.')) {
		return style
	}
	let skin = componentsSkinParams[style.skin] ? style.skin : last(style.skin.split('.'))
	if (deprecatedSkinsFallbacks[skin]) {
		skin = deprecatedSkinsFallbacks[skin]
	}
	if (componentTypeToSkins[componentClassName]) {
		skin =
			componentTypeToSkins[componentClassName].validSkins[skin] ||
			componentTypeToSkins[componentClassName].defaultSkin
	}
	const defaults = componentsSkinParams[skin] || { types: {}, defaults: {} }

	return {
		...style,
		skin,
		style: {
			...style.style,
			properties: getProperties(
				style.spx,
				style.style?.properties,
				defaults,
				isSiteFromStudio,
				skin,
				staticMediaUrl,
				themeFontMap,
				style.style?.propertiesOverride
			),
		},
	}
}

const getStaticSpxValue = (spx: Spx | undefined) => {
	return spx && spx.resolverType === 'scale' ? `${100 / SPX_CONTAINER_WIDTH_FOR_STATIC_CSS} * var(--one-unit)` : '1px'
}

const shouldIgnoreProp = (prop: string) => {
	const propsToIgnore = ['f0', 'f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'f9', 'f10']
	return propsToIgnore.includes(prop)
}
const handleThemeColor = (
	acc: Record<string, string | number>,
	prop: string,
	value: string,
	properties: { [id: string]: string }
) => {
	acc[`--${prop}`] = `var(--${value})`
	if (isUndefined(properties[`alpha-${prop}`])) {
		acc[`--alpha-${prop}`] = 1
	}
}

const handleGradient = (gradients: Array<Gradient>, siteColors: Array<string>) => {
	return {
		'--bg-gradient': gradients
			.reduce(
				(acc, gradient) => acc + color.generateGradient(gradient.fill, gradient.opacity ?? 1, siteColors) + ',',
				''
			)
			.slice(0, -1),
	}
}

export const getStyleCssVars = (
	style: Style,
	isScalePropExperimentOn: boolean = false,
	themeSpx: Spx | undefined = undefined,
	siteColors: Array<string> = [],
	componentsSkinParams: Record<string, SkinDefaultsData> = {}
): Record<string, string | number> => {
	const {
		skin,
		spx,
		themeOverrides,
		cssProperties,
		cssCustomProperties,
		style: { properties } = {},
	} = style

	const skinParamsTypes = componentsSkinParams[skin] ? componentsSkinParams[skin].types : {}

	const unifiedProperties = { ...properties, ...cssProperties, ...cssCustomProperties } // TODO: remove properties once we remove the old style api

	const gradient = style.style?.background?.gradient?.length
		? handleGradient(style.style.background.gradient, siteColors)
		: skinParamsTypes.gradient
			? { '--bg-gradient': componentsSkinParams[skin].defaults.gradient }
			: {}

	return {
		...Object.entries(unifiedProperties).reduce<Record<string, string | number>>((acc, [prop, value]) => {
			// Check if the original or fixed (i.e. without the $ prefix) key has a type
			const paramType = skinParamsTypes[prop] || skinParamsTypes[`$${prop}`]
			const shouldHandleHexColor =
				typeof value === 'string' && color.isHexValue(value) && paramType !== 'CSS_COLOR'
			if (shouldIgnoreProp(prop)) {
				return acc
			} else if (prop.startsWith('alpha') || paramType === 'ALPHA') {
				// we need to check if there is the color property for alpha property
				const colorParamValue = unifiedProperties[`${prop.replace('alpha-', '')}`]
				if (colorParamValue === 'transparent') {
					// and set alpha to 0 when the color is transparent
					acc[`--${prop}`] = 0
				} else {
					acc[`--${prop}`] = parseFloat(value)
				}
			} else if (color.isThemeColor(value)) {
				handleThemeColor(acc, prop, value, unifiedProperties)
			} else if (isThemeFont(value)) {
				acc[`--${prop}`] = `var(--${value})`
			} else if (paramType === 'COLOR' || shouldHandleHexColor) {
				acc[`--${prop}`] = color.getSplitRgbValuesStringWithoutAlpha(value)!
				if (value === 'transparent') {
					// set alpha channel to 0 explicitly when the color is transparent
					acc[`--alpha-${prop}`] = 0
				} else if (isUndefined(unifiedProperties[`alpha-${prop}`])) {
					acc[`--alpha-${prop}`] = color.getColorAlpha(value)!
				}
			} else if (typeof value === 'string' && value.startsWith('{') && value.endsWith('}')) {
				try {
					acc[`--${prop}`] = JSON.parse(value).value
				} catch (e) {
					const innerVal = value.slice(1, -1)
					// handle the case of {color_x} value
					if (color.isThemeColor(innerVal)) {
						handleThemeColor(acc, prop, innerVal, unifiedProperties)
					} else {
						acc[`--${prop}`] = value
					}
				}
			} else if (prop.includes('boxShadowToggleOn')) {
				// TODO temp fix until we merge editor-elements
				if (value === 'false') {
					acc[`--${prop}`] = 'none'
				}
			} else if (paramType === 'BOX_SHADOW' && unifiedProperties[`boxShadowToggleOn-${prop}`] === 'false') {
				// if boxShadowToggleOn is false, override the value for box-shadow with `none`, else, don't override the value.
				acc[`--${prop}`] = 'none'
			} else {
				acc[`--${prop}`] = value
			}

			return acc
		}, {}),
		...(isScalePropExperimentOn ? { '--static-spx': getStaticSpxValue(spx) } : null),
		...(isScalePropExperimentOn && themeOverrides
			? { '--theme-spx-ratio': getThemeSpxRatio(themeSpx, themeOverrides) }
			: null),
		...gradient,
	}
}

export const getSkinParams = (
	skin: string,
	componentsSkinParams: Record<string, SkinDefaultsData> = {}
): SkinDefaultsData => {
	return componentsSkinParams[skin]
}

export const correctInlineColorStyle = (
	html: string,
	overrideColor: string,
	brightness: number,
	colorMap: ColorMap,
	fontColorMap: ThemeFontColorMap
) => {
	return html.replace(/<[^/>]+>/gm, (htmlOpenTag) => {
		const tagName = getTagName(htmlOpenTag)
		const attributes = getTagAttributes(htmlOpenTag)
		const inlineStyles = attributes.style && getInlineStyle(attributes.style)
		const colorClass = attributes.class && color.getColorClass(attributes.class)
		const fontClass = attributes.class && getFontClass(attributes.class)

		let desktopColor: string | undefined

		if (inlineStyles && inlineStyles.color) {
			desktopColor = inlineStyles.color
		} else if (colorClass) {
			desktopColor = color.getFromColorMap(colorClass, colorMap)
		} else if (fontClass && fontColorMap[fontClass]) {
			desktopColor = color.getFromColorMap(fontColorMap[fontClass], colorMap)
		}

		if (!desktopColor) {
			return htmlOpenTag
		}

		const newInlineStyles = inlineStyles ? { ...inlineStyles } : {}

		const isOverrideColorValid = color.isValidColor(overrideColor)
		if (!overrideColor || !isOverrideColorValid) {
			newInlineStyles.color = `${color.brighten(desktopColor, brightness)}`
		} else {
			newInlineStyles.color = color.isRgbValues(overrideColor)
				? `rgba(${overrideColor})`
				: `${color.formatColor(color.getFromColorMap(overrideColor, colorMap), '1')}`
		}

		attributes.style = toInlineStyle(newInlineStyles)

		return toHtmlOpenTag(tagName, attributes)
	})
}

export const getStyleItemsToOmit = (style: UnresolvedVariantRelation, id: string) => {
	return {
		[id]: false,
		// when removing the variantRelation, we need to remove the 'to' value as well
		[removeHash(style.to)!]: false,
	}
}

const getThemeSpxRatio = (themeSpx?: Spx, themeOverrides?: { spx?: Spx }) => {
	if (themeSpx && themeOverrides?.spx) {
		const overrideResolver = themeOverrides.spx.resolverType
		const refWidthRatio = themeOverrides.spx.refWidth / themeSpx.refWidth
		if (overrideResolver === 'fixed') {
			return `${refWidthRatio}px`
		} else {
			return `${refWidthRatio * (100 / themeSpx.refWidth)} * var(--one-unit)`
		}
	}
	return 'unset'
}
